import { CustomReportParamTypes, CustomReportTypes } from '../constants';

import {
  testCustomReport as testCustomReportApi,
  validateCustomReport as validateCustomReportApi,
} from '../api';

import { fetchingError } from '../../store/actions';
import { errorNotification, successNotification } from '../../store/notificationActions';
import { getParamNamesForQuery } from '../helpers';
import pick from 'lodash/pick';

import {
  VERIFY_PARAM_OPTIONS_QUERY,
} from '../../../reports/customReports/store/types';

export const fetchCustomReportsFactory = ({
  fetchApi,
  action,
  actionSuccess,
  actionFailure,
}) => () => async dispatch => {
  dispatch({
    type: action,
  });

  try {
    const customReports = await fetchApi();

    return dispatch({
      type: actionSuccess,
      customReports,
    });
  } catch (error) {
    dispatch({
      type: actionFailure,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};


export const fetchCustomReportParamsFactory = ({
  action,
  actionSuccess,
  actionFailure,
}) => (queryParams, paramsValues) => async dispatch => {
  dispatch({
    type: action,
  });

  try {
    const responses = await Promise.all(Object.values(queryParams).map(async param => {
      if (param.type === CustomReportParamTypes.query) {
        const paramNames = getParamNamesForQuery(param.query);

        if (!paramNames.every(paramName => !!paramsValues[paramName])
        ) {
          return {
            name: param.id || param.name,
            options: [],
          };
        }

        const data = await testCustomReportApi({
          sql: param.query,
          params: pick(paramsValues, paramNames),
        });

        return {
          name: param.id || param.name,
          options: data.rows.map(row => ({
            value: row[param.value],
            label: row[param.label],
          })),
        };
      }

      return null;
    }));

    const params = responses.reduce((result, param) => {
      if (param) {
        result[param.name] = param.options;
      }
      return result;
    }, {});

    dispatch({
      type: actionSuccess,
      params,
    });
  } catch (error) {
    dispatch({
      type: actionFailure,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(`Testing Custom Report failed: "${error.message}"`));
      return;
    }
    throw error;
  }
};

export const selectCustomReportFactory = ({ action }) => customReportId => ({
  type: action,
  customReportId,
});

const runReport = async ({ customReportType, queries, query, params }) => {
  if (customReportType === CustomReportTypes.REPORT_SET) {
    const reports = await Promise.all(queries.map(async query => {
      const result = await testCustomReportApi(query);
      return { name: query.name, ...result };
    }));
    return { reports };
  }
  return await testCustomReportApi({ sql: query, params });
};

const runValidatorReport = async ({ customReportType, queries, query, params }) => {
  if (customReportType === CustomReportTypes.REPORT_SET) {
    const reports = await Promise.all(queries.map(async query => {
      const result = await validateCustomReportApi(query);
      return { name: query.name, ...result };
    }));
    return { reports };
  }
  return await validateCustomReportApi({ sql: query, params });
};

export const runCustomReportFactory = ({
  action,
  actionSuccess,
  actionFailure,
  responseTransform,
  successNotificationText = null,
}) => ({
  query,
  params = {},
  name,
  customReportType = CustomReportTypes.REPORT,
  queries = [],
  onSuccess,
  onFailure,
}) => async dispatch => {
  dispatch({
    type: action,
  });

  try {
    const response = action === VERIFY_PARAM_OPTIONS_QUERY
      ? await runValidatorReport({ customReportType, queries, query, params })
      : await runReport({ customReportType, queries, query, params });

    const result = { name, customReportType, ...response };

    const transformedResponse = responseTransform
      ? responseTransform(result)
      : result;

    dispatch({
      type: actionSuccess,
      response: transformedResponse,
    });
    if (successNotificationText) {
      dispatch(successNotification(successNotificationText));
    }
    if (onSuccess) {
      onSuccess(transformedResponse);
    }
  } catch (error) {
    dispatch({
      type: actionFailure,
    });
    dispatch(errorNotification(error.message));
    if (onFailure) {
      onFailure(error);
    }
  }
};
